/**
 * MealList component
 * Displays a grid of meal cards with loading and empty states
 * Mobile-first responsive layout
 */

import { useTranslation } from 'react-i18next';
import { MealCard } from './MealCard';
import { SkeletonCard } from '@/components/ui/SkeletonCard';
import { EmptyState } from '@/components/ui/EmptyState';
import type { Meal } from '@/types/api';

interface MealListProps {
  meals: Meal[];
  isLoading?: boolean;
  onClearFilters?: () => void;
}

/**
 * MealList component
 *
 * Features:
 * - Responsive grid layout (1-4 columns)
 * - Loading skeleton state
 * - Empty state when no meals
 * - Optimized spacing for mobile/tablet/desktop
 *
 * @example
 * ```tsx
 * <MealList meals={meals} isLoading={isLoading} onClearFilters={handleClear} />
 * ```
 */
export function MealList({ meals, isLoading, onClearFilters }: MealListProps) {
  const { t } = useTranslation();

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
        {Array.from({ length: 8 }).map((_, index) => (
          <SkeletonCard key={index} lines={2} />
        ))}
      </div>
    );
  }

  // Empty state
  if (meals.length === 0) {
    return (
      <EmptyState
        icon={
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
        title={t('meals.noMealsFound')}
        description={t('meals.adjustSearchFilter')}
        action={
          onClearFilters
            ? {
                label: t('meals.clearFilters'),
                onClick: onClearFilters,
              }
            : undefined
        }
      />
    );
  }

  // Meal grid
  return (
    <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
      {meals.map((meal) => (
        <MealCard key={meal.id} meal={meal} />
      ))}
    </div>
  );
}
